import Phaser from 'phaser';
import pawSvg from './pawPrint.svg?raw';
import { DIRT_HALF, VERGE_HALF, dressRoad, pawExtent, type PawPrint, type RoadDressing, type RoadSample } from './dirtRoad';
import type { RoadMap } from './roadPath';

function ribbon(graphics: Phaser.GameObjects.Graphics, samples: RoadSample[], from: number, to: number, half: number, color: number, alpha: number): void {
  graphics.fillStyle(color, alpha);
  for (let index = from + 1; index <= to; index += 1) {
    const a = samples[index - 1] as RoadSample;
    const b = samples[index] as RoadSample;
    const ax = a.x + a.nx * half;
    const ay = a.y + a.ny * half;
    const bx = b.x + b.nx * half;
    const by = b.y + b.ny * half;
    const cx = b.x - b.nx * half;
    const cy = b.y - b.ny * half;
    const dx = a.x - a.nx * half;
    const dy = a.y - a.ny * half;
    graphics.fillTriangle(ax, ay, bx, by, cx, cy);
    graphics.fillTriangle(ax, ay, cx, cy, dx, dy);
  }
}

function stroke(graphics: Phaser.GameObjects.Graphics, points: ReadonlyArray<{ x: number; y: number }>, width: number, color: number, alpha: number): void {
  if (points.length < 2) return;
  graphics.lineStyle(width, color, alpha);
  graphics.beginPath();
  graphics.moveTo(points[0]?.x ?? 0, points[0]?.y ?? 0);
  for (let index = 1; index < points.length; index += 1) {
    const point = points[index];
    if (point) graphics.lineTo(point.x, point.y);
  }
  graphics.strokePath();
}

/** Shared paw icon. Authored at 320×256 and rasterized larger for device pixels. */
export const PAW_TEX = 'cc-paw-print';
/** Brown of the SVG, shown over the dirt. */
const PAW_ALPHA = 0.68;
/** Clean dirt kept around each print, past the icon itself. */
const PAW_MARGIN = 10;

/**
 * Texture size for the on-screen print. The SVG is the high-resolution source;
 * this matches device pixels so the GPU does not blur a huge texture down.
 */
export function pawRasterSize(canvas?: HTMLCanvasElement | null): { width: number; height: number } {
  const extent = pawExtent();
  const dpr = typeof window === 'undefined' ? 3 : window.devicePixelRatio || 1;
  const css = canvas?.clientWidth ?? 0;
  const buffer = canvas?.width ?? 0;
  const perGame = buffer > 0 && css > 0 ? (css * dpr) / buffer : Math.max(2, Math.min(3, dpr));
  const scale = Math.max(1, perGame);
  return {
    width: Math.max(8, Math.round(extent.width * scale)),
    height: Math.max(8, Math.round(extent.length * scale)),
  };
}

function drawPaw(image: CanvasImageSource, width: number, height: number): HTMLCanvasElement | null {
  const fine = document.createElement('canvas');
  fine.width = width * 2;
  fine.height = height * 2;
  const fineContext = fine.getContext('2d');
  if (!fineContext) return null;
  fineContext.drawImage(image, 0, 0, fine.width, fine.height);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) return null;
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  context.drawImage(fine, 0, 0, width, height);
  return canvas;
}

/** Paint the shared SVG once, sized for this screen, and reuse it for every print. */
export function installPawTexture(scene: Phaser.Scene): Promise<void> {
  if (scene.textures.exists(PAW_TEX)) return Promise.resolve();
  const size = pawRasterSize(scene.game.canvas);
  const blob = new Blob([pawSvg], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  return new Promise((resolve) => {
    const image = new Image();
    const finish = () => {
      URL.revokeObjectURL(url);
      resolve();
    };
    image.onload = () => {
      const canvas = drawPaw(image, size.width, size.height);
      const texture = canvas ? scene.textures.addCanvas(PAW_TEX, canvas) : null;
      texture?.setFilter(Phaser.Textures.FilterMode.LINEAR);
      finish();
    };
    image.onerror = finish;
    image.src = url;
  });
}

function nearPrint(x: number, y: number, prints: readonly PawPrint[]): boolean {
  const extent = pawExtent();
  const along = extent.length / 2 + PAW_MARGIN;
  const across = extent.width / 2 + PAW_MARGIN;
  for (const print of prints) {
    const turn = Math.cos(print.angle);
    const rise = Math.sin(print.angle);
    const dx = x - print.x;
    const dy = y - print.y;
    const lx = turn * dx + rise * dy;
    const ly = -rise * dx + turn * dy;
    if (Math.abs(lx) <= along && Math.abs(ly) <= across) return true;
  }
  return false;
}

function cutRut(points: ReadonlyArray<{ x: number; y: number }>, dressing: RoadDressing): { x: number; y: number }[][] {
  const runs: { x: number; y: number }[][] = [];
  let run: { x: number; y: number }[] = [];
  for (const point of points) {
    if (nearPrint(point.x, point.y, dressing.paws)) {
      if (run.length > 1) runs.push(run);
      run = [];
    } else {
      run.push(point);
    }
  }
  if (run.length > 1) runs.push(run);
  return runs;
}

function tuft(graphics: Phaser.GameObjects.Graphics, dressing: RoadDressing): void {
  for (const blade of dressing.tufts) {
    if (nearPrint(blade.x, blade.y, dressing.paws)) continue;
    const tangentX = -blade.ny;
    const tangentY = blade.nx;
    graphics.lineStyle(3.2, blade.color, 0.95);
    for (const lean of [-0.55, 0, 0.5]) {
      const angle = blade.lean + lean;
      const dx = blade.nx * Math.cos(angle) + tangentX * Math.sin(angle);
      const dy = blade.ny * Math.cos(angle) + tangentY * Math.sin(angle);
      graphics.beginPath();
      graphics.moveTo(blade.x, blade.y);
      graphics.lineTo(blade.x + dx * blade.len, blade.y + dy * blade.len);
      graphics.strokePath();
    }
  }
}

/** One drawing of the whole curve. Nothing here is a tiled road sprite. */
export function paintDirtRoad(scene: Phaser.Scene, map: RoadMap, count: number): Phaser.GameObjects.Container {
  const dressing = dressRoad(map, count);
  const layer = scene.add.container(0, 0).setDepth(2);
  const graphics = scene.add.graphics();
  layer.add(graphics);
  const { samples } = dressing;
  const last = samples.length - 1;
  const shadow = samples.map((sample) => ({ ...sample, x: sample.x + 3, y: sample.y + 5 }));
  ribbon(graphics, shadow, 0, last, DIRT_HALF + 4, 0x2a1c12, 0.16);
  ribbon(graphics, samples, 0, last, VERGE_HALF, 0x6e9440, 0.38);
  ribbon(graphics, samples, 0, last, VERGE_HALF - 6, 0x3e6a28, 0.5);
  ribbon(graphics, samples, 0, last, DIRT_HALF + 3, 0x6a3d22, 1);
  ribbon(graphics, samples, 0, last, DIRT_HALF, 0xb8733a, 1);
  ribbon(graphics, samples, 0, last, 7, 0xe2b56a, 0.28);
  for (const run of cutRut(dressing.leftRut, dressing)) stroke(graphics, run, 5.2, 0x4a2a14, 0.9);
  for (const run of cutRut(dressing.rightRut, dressing)) stroke(graphics, run, 5.2, 0x4a2a14, 0.9);
  for (const run of cutRut(
    dressing.leftRut.map((point) => ({ x: point.x - 1.6, y: point.y - 1.6 })),
    dressing,
  )) {
    stroke(graphics, run, 2, 0xe7c98a, 0.55);
  }
  for (const pebble of dressing.pebbles) {
    if (nearPrint(pebble.x, pebble.y, dressing.paws)) continue;
    graphics.fillStyle(0x4a3428, 1);
    graphics.fillEllipse(pebble.x, pebble.y, pebble.rx * 2 + 2, pebble.ry * 2 + 2);
    graphics.fillStyle(pebble.color, 1);
    graphics.fillEllipse(pebble.x - 0.4, pebble.y - 0.4, pebble.rx * 2, pebble.ry * 2);
    graphics.fillStyle(0xfff6e4, 0.85);
    graphics.fillEllipse(pebble.x - pebble.rx * 0.3, pebble.y - pebble.ry * 0.35, Math.max(1.5, pebble.rx), Math.max(1.2, pebble.ry * 0.7));
  }
  tuft(graphics, dressing);
  const extent = pawExtent();
  const texture = scene.textures.get(PAW_TEX);
  if (texture.key !== '__MISSING') texture.setFilter(Phaser.Textures.FilterMode.LINEAR);
  for (const print of dressing.paws) {
    const stamp = scene.add.image(print.x, print.y, PAW_TEX);
    stamp.setDisplaySize(extent.width, extent.length);
    stamp.setRotation(print.angle + Math.PI / 2);
    stamp.setAlpha(PAW_ALPHA);
    layer.add(stamp);
  }
  return layer;
}
